import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Minus } from 'lucide-react';

const content = {
  de: {
    eyebrow: 'Über EnBet',
    headline: 'KI IST EIN WERKZEUG. DIE VERANTWORTUNG BLEIBT BEI MIR.',
    intro: 'Ich habe über 100 Retail-Standorte mit aufgebaut und rund 9 Millionen Euro Monatsumsatz verantwortet. Dazu eine AI-Business-Strategy-Weiterbildung. Ich weiß, was auf dem Papier gut aussieht und im Betrieb trotzdem scheitert. Genau danach wähle ich aus, was wir bauen.',
    bullets: [],
    closing: '',
    facts: [
      '100 Standorte skaliert',
      'ca. 9 Mio. Monatsumsatz verantwortet',
      'AI Business Strategy Weiterbildung 2025',
    ],
  },
  en: {
    eyebrow: 'About EnBet',
    headline: 'AI IS A TOOL. THE RESPONSIBILITY STAYS WITH ME.',
    intro: 'I helped build over 100 retail locations and managed around 9 million euros in monthly revenue. Plus an AI Business Strategy programme. I know what looks good on paper and still fails in operations. That is exactly how I choose what we build.',
    bullets: [],
    closing: '',
    facts: [
      '100 locations scaled',
      'ca. 9M monthly revenue managed',
      'AI Business Strategy certification 2025',
    ],
  },
};

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-80px' } };

export default function AboutSection() {
  const { lang } = useLanguage();
  const c = content[lang];

  return (
    <section id="about-sebastian-funke" data-testid="about-section" className="bg-[#f7f7f7] py-24 md:py-32">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">

          {/* Left: Portrait */}
          <motion.div {...fade} transition={{ duration: 0.5 }} className="lg:col-span-2">
            <div className="aspect-[3/4] overflow-hidden rounded-2xl">
              <img
                data-testid="about-portrait"
                src="/assets/sebastian-funke.jpg"
                alt="Sebastian Funke — KI-Berater für den Mittelstand (EnBet Consulting)"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width="600"
                height="800"
              />
            </div>
          </motion.div>

          {/* Right: Content */}
          <div className="lg:col-span-3">
            <motion.p {...fade} transition={{ duration: 0.4 }}
              className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {c.eyebrow}
            </motion.p>

            <motion.h2 {...fade} transition={{ duration: 0.4, delay: 0.05 }}
              data-testid="about-headline"
              className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight leading-[1.3] mb-6"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {c.headline}
            </motion.h2>

            <motion.p {...fade} transition={{ duration: 0.4, delay: 0.1 }}
              className="text-[#1f1f1f]/70 text-base leading-relaxed mb-8"
              style={{ fontFamily: "'Manrope', sans-serif" }}>
              {c.intro}
            </motion.p>

            <div className="space-y-4 mb-8">
              {c.bullets.map((b, i) => (
                <motion.div key={i} {...fade} transition={{ duration: 0.4, delay: 0.15 + i * 0.05 }}
                  className="flex items-start gap-3">
                  <Minus size={16} className="text-[#befa0a] mt-1.5 flex-shrink-0" />
                  <p className="text-[#1f1f1f]/60 text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {b}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.p {...fade} transition={{ duration: 0.4, delay: 0.35 }}
              className="text-[#1f1f1f] text-sm leading-relaxed font-medium mb-10"
              style={{ fontFamily: "'Manrope', sans-serif" }}>
              {c.closing}
            </motion.p>

            {/* Micro Facts */}
            <motion.div {...fade} transition={{ duration: 0.4, delay: 0.4 }}
              data-testid="about-facts"
              className="flex flex-wrap gap-3">
              {c.facts.map((fact, i) => (
                <span key={i}
                  className="text-[#1f1f1f]/50 text-[11px] uppercase tracking-[0.1em] border border-[#e0e0e0] px-3 py-1.5"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {fact}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
